/**
 * Normalizes `/a/get-values-metrics` style payloads for angle-like series.
 *
 * Defensive fixes for common backend/query issues:
 * - Missing ORDER BY → rows arrive as [frame, angle] pairs out of frame order
 * - Duplicate frame keys → pick last sample (stable)
 *
 * Preserves API shape: `values` remains an array of 1-tuples `[[v], ...]`.
 */

const DEFAULT_NOMINAL_HZ = 85;

function integerFraction(col) {
  if (!col.length) return 0;
  let n = 0;
  for (const x of col) {
    if (isFiniteNumber(x) && Math.abs(x - Math.round(x)) < 1e-3) n += 1;
  }
  return n / col.length;
}

function keysLookLikeFrameIndices(keys, frameGapMax) {
  if (!keys.length) return false;
  if (maxSortedUniqueGap(keys) > frameGapMax) return false;
  return integerFraction(keys) >= 0.85;
}

function isFiniteNumber(x) {
  return typeof x === 'number' && Number.isFinite(x);
}

function maxConsecutiveAbsDelta(arr) {
  if (!arr || arr.length < 2) return 0;
  let m = 0;
  for (let i = 1; i < arr.length; i++) {
    m = Math.max(m, Math.abs(arr[i] - arr[i - 1]));
  }
  return m;
}

function maxSortedUniqueGap(keys) {
  if (!keys.length) return 0;
  const s = [...keys].sort((a, b) => a - b);
  const uniq = [];
  for (const k of s) {
    if (!uniq.length || uniq[uniq.length - 1] !== k) uniq.push(k);
  }
  if (uniq.length < 2) return 0;
  let m = 0;
  for (let i = 1; i < uniq.length; i++) {
    m = Math.max(m, uniq[i] - uniq[i - 1]);
  }
  return m;
}

function mergeByFrameColumn(raw, frameIdx, valueIdx) {
  const decorated = raw.map((row) => ({
    k: row[frameIdx],
    v: row[valueIdx],
  }));
  decorated.sort((a, b) => a.k - b.k);
  let dedupeCollisions = 0;
  const merged = [];
  for (let i = 0; i < decorated.length; i++) {
    if (
      merged.length &&
      decorated[i].k === merged[merged.length - 1].k
    ) {
      dedupeCollisions += 1;
      merged[merged.length - 1] = decorated[i];
    } else {
      merged.push(decorated[i]);
    }
  }
  const keys = decorated.map((d) => d.k);
  const vals = merged.map((o) => o.v);
  return {
    normalizedRows: merged.map((o) => [o.v]),
    dedupeCollisions,
    maxFrameGap: maxSortedUniqueGap(keys),
    maxAngleDelta: maxConsecutiveAbsDelta(vals),
  };
}

function allRowsAreTwoNumberColumns(values) {
  if (!Array.isArray(values) || values.length === 0) return false;
  return values.every(
    (row) =>
      Array.isArray(row) &&
      row.length >= 2 &&
      isFiniteNumber(row[0]) &&
      isFiniteNumber(row[1])
  );
}

function allRowsAreOneNumberColumns(values) {
  if (!Array.isArray(values) || values.length === 0) return false;
  return values.every(
    (row) =>
      Array.isArray(row) &&
      row.length === 1 &&
      isFiniteNumber(row[0])
  );
}

/**
 * If values look like [[frame, angle], ...] out of order, sort by frame and dedupe.
 * If values are [[angle], ...], return angles unchanged (only coerce shape).
 */
export function normalizeAnglesLikeSeries(res, options = {}) {
  const { trialId = null, metricName = null, logDebug = false } = options;
  if (!res || !Array.isArray(res.values)) {
    return res;
  }

  const raw = res.values;
  const beforeYs = raw.map((row) => (Array.isArray(row) ? row[0] : NaN)).filter(isFiniteNumber);
  const maxDeltaBefore =
    beforeYs.length >= 2 ? maxConsecutiveAbsDelta(beforeYs) : 0;

  let normalizedRows = raw;
  let sortApplied = false;
  let dedupeFrames = 0;

  if (allRowsAreTwoNumberColumns(raw)) {
    const frameGapMax = Math.max(24, Math.ceil(raw.length / 40));
    const planA = mergeByFrameColumn(raw, 0, 1);
    const planB = mergeByFrameColumn(raw, 1, 0);
    const keysA = raw.map((r) => r[0]);
    const keysB = raw.map((r) => r[1]);
    const okA = keysLookLikeFrameIndices(keysA, frameGapMax);
    const okB = keysLookLikeFrameIndices(keysB, frameGapMax);

    let chosen = null;
    if (okA && okB) {
      chosen =
        planA.maxAngleDelta <= planB.maxAngleDelta + 1e-6 ? planA : planB;
    } else if (okA) {
      chosen = planA;
    } else if (okB) {
      chosen = planB;
    }

    if (chosen) {
      sortApplied = true;
      dedupeFrames = chosen.dedupeCollisions;
      normalizedRows = chosen.normalizedRows;
    } else {
      normalizedRows = raw.map((row) => [row[0]]);
    }
  } else if (allRowsAreOneNumberColumns(raw)) {
    normalizedRows = raw.map((row) => [row[0]]);
  } else {
    normalizedRows = raw.map((row) => {
      if (!Array.isArray(row) || row.length === 0) return [NaN];
      const first = row.find((x) => isFiniteNumber(x));
      return [first !== undefined ? first : NaN];
    });
  }

  const ys = normalizedRows.map((r) => r[0]).filter(isFiniteNumber);
  const maxDeltaAfter = ys.length >= 2 ? maxConsecutiveAbsDelta(ys) : 0;

  const diagnostics = {
    rowCount: normalizedRows.length,
    rawRowCount: raw.length,
    sortApplied,
    dedupeCollisions: dedupeFrames,
    maxAbsDeltaBefore: maxDeltaBefore,
    maxAbsDeltaAfter: maxDeltaAfter,
  };

  if (logDebug) {
    const sampleWindow = (t0, t1) => {
      const hz =
        res.duration_sec && ys.length
          ? ys.length / res.duration_sec
          : DEFAULT_NOMINAL_HZ;
      const i0 = Math.max(0, Math.floor(t0 * hz));
      const i1 = Math.min(ys.length, Math.ceil(t1 * hz));
      const slice = [];
      for (let i = i0; i < i1; i += Math.max(1, Math.floor((i1 - i0) / 12))) {
        slice.push({ i, t: i / hz, y: ys[i] });
      }
      return slice;
    };
    console.info('[angle-series]', {
      trialId,
      metricName,
      ...diagnostics,
      duration_sec: res.duration_sec,
      window_18_24s: sampleWindow(18, 24),
    });
  }

  return {
    ...res,
    values: normalizedRows,
    _angleSeriesDiagnostics: diagnostics,
  };
}

/**
 * Effective duration for mapping sample index → seconds.
 * Prefer HTML5 video duration when available (scrub/playback source of truth),
 * then server `duration_sec`, else null (caller uses nominal Hz).
 */
export function resolveSeriesDurationSec(res, videoDurationSec) {
  const fromVideo =
    isFiniteNumber(videoDurationSec) && videoDurationSec > 0
      ? videoDurationSec
      : null;
  const fromApi = res && isFiniteNumber(res.duration_sec) && res.duration_sec > 0
    ? res.duration_sec
    : null;
  return fromVideo ?? fromApi ?? null;
}

/**
 * Samples per second for index → time(s): i / hz.
 */
export function resolveSampleRateHz(res, valuesLength, videoDurationSec, nominalHz = DEFAULT_NOMINAL_HZ) {
  const dur = resolveSeriesDurationSec(res, videoDurationSec);
  if (dur != null && valuesLength > 0) {
    return valuesLength / dur;
  }
  return nominalHz;
}

export function shouldLogAngleSeriesInstrumentation(trialId) {
  if (typeof window === 'undefined') return false;
  if (window.__EVOVISION_ANGLE_GRAPH_DEBUG__) return true;
  const id = trialId != null ? String(trialId) : '';
  if (id === '850' && process.env.NODE_ENV !== 'production') return true;
  return false;
}
